import { useNode } from "@craftjs/core";
import { cn } from "@/lib/utils";
import { applyStyleProps, defaultStyleProps, type ResponsiveStyleProps } from "@/lib/craft/style-props";
import { GridGalleryBlockSettings } from "@/components/craft/settings/GridGalleryBlockSettings";

export type GalleryImage = { url: string; mediaId?: string | null; alt?: string };

type GridGalleryBlockProps = {
  images: GalleryImage[];
  columns?: 2 | 3 | 4;
  style?: ResponsiveStyleProps;
};

export function GridGalleryBlock({
  images = [],
  columns = 3,
  style = defaultStyleProps(),
}: GridGalleryBlockProps) {
  const {
    connectors: { connect, drag },
    selected,
    id,
  } = useNode((node) => ({ selected: node.events.selected, id: node.id }));
  const { style: inlineStyle, className: styleClass } = applyStyleProps(style, "desktop", id);

  const gridCols = { 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4" }[columns];

  return (
    <div
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={cn("my-6", `craft-node-${id}`, styleClass, selected && "outline outline-2 outline-dashed outline-primary/30")}
      style={inlineStyle}
    >
      <div className={cn("grid gap-3", gridCols)}>
        {images.length ? (
          images.map((img, i) => (
            <img
              key={i}
              src={img.url}
              alt={img.alt ?? ""}
              className="aspect-square w-full rounded-lg object-cover"
            />
          ))
        ) : (
          <p className="col-span-full py-8 text-center text-sm text-muted-foreground">Add images in settings</p>
        )}
      </div>
    </div>
  );
}

GridGalleryBlock.craft = {
  displayName: "Gallery",
  props: { images: [], columns: 3, style: defaultStyleProps() },
  rules: { canDrag: () => true },
  related: { settings: GridGalleryBlockSettings },
};
